import { css, useTheme } from '@emotion/react';
import { Delete, MoreOne, Refresh } from '@icon-park/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Upload } from '@didaskill/domain/uploads/types';
import { Button, Dropdown } from '@didaskill/ui/design-system';

import messages from './ProfilePictureItem.messages';
import { imageStyle } from './ProfilePictureItem.styles';

export type ProfilePictureItemProps = {
  /** Picture to display */
  picture: Upload;
  /** Define if the picture is currently selected in a set of pictures. */
  isSelected?: boolean;
  onChangeSelected?: (pictureId: string) => void;
  /** Callback triggered on the click of the Delete entry. */
  onDelete?: (pictureId: string) => void;
};

export const ProfilePictureItemBase = ({
  picture,
  isSelected = false,
  onChangeSelected = noop,
  onDelete = noop,
}: ProfilePictureItemProps) => {
  const theme = useTheme();

  const proxyOnChangeSelected = useCallback(
    () => onChangeSelected(picture.id),
    [onChangeSelected, picture],
  );

  const proxyOnDelete = useCallback(() => onDelete(picture.id), [onDelete, picture]);

  return (
    <div
      css={css`
        position: relative;
        width: 125px;
        height: 125px;
        border-radius: 4px;

        transition: ${theme.transitions.default()};
      `}
    >
      <div css={imageStyle(theme, picture.href)} className={cn(isSelected && 'SELECTED')}>
        <Dropdown
          menu={
            <Dropdown.List
              css={css`
                all: none;
                width: 257px;
              `}
            >
              <Dropdown.ListItem
                icon={<Refresh />}
                label={<FormattedMessage {...messages.DEFINE_PROFIL_PICTURE} />}
                onClick={proxyOnChangeSelected}
              />
              <Dropdown.ListItem
                icon={<Delete />}
                label={<FormattedMessage {...messages.DELETE_PROFIL_PICTURE} />}
                onClick={proxyOnDelete}
              />
            </Dropdown.List>
          }
        >
          <Button
            css={css`
              position: absolute;
              top: 8px;
              right: 8px;
            `}
            outline
            icon={<MoreOne />}
            size={Button.SIZES.SMALL}
          />
        </Dropdown>
      </div>
    </div>
  );
};

ProfilePictureItemBase.displayName = 'ProfilePictureItem';

export default memo(ProfilePictureItemBase);
